import { type Static, Type as t } from "@sinclair/typebox";
import {
  AssignablePolicyLevelSchema,
  CategorySchema,
  GalleryStateSchema,
  Gid,
  IsoDateTime,
  nullable,
  Tag,
} from "./primitives";

/**
 * Policy as the client sees it (U-15b).
 *
 * The decisive rule lives in the TYPES: a `block` gallery is a different schema from an open one and
 * that schema has no `thumb` property (and forbids unknown ones). A server that tries to send a
 * thumbnail address for a blocked gallery fails response validation instead of leaking it.
 */
export const PolicyNone = t.Object({ level: t.Literal("none") }, { additionalProperties: false });
export const PolicyWarn = t.Object(
  { level: t.Literal("warn"), matchedTags: t.Array(Tag, { minItems: 1 }) },
  { additionalProperties: false },
);
export const PolicyBlock = t.Object(
  { level: t.Literal("block"), matchedTags: t.Array(Tag, { minItems: 1 }) },
  { additionalProperties: false },
);
export const GalleryPolicy = t.Union([PolicyNone, PolicyWarn, PolicyBlock]);
export type GalleryPolicyType = Static<typeof GalleryPolicy>;

/** Fields every gallery response has, whatever its policy. No thumbnail here on purpose. */
const baseProps = {
  gid: Gid,
  token: t.String({ pattern: "^[0-9a-f]{10}$" }),
  state: GalleryStateSchema,
  title: t.String(),
  titleJpn: nullable(t.String()),
  category: CategorySchema,
  uploader: nullable(t.String()),
  posted: nullable(IsoDateTime),
  filecount: t.Integer({ minimum: 0 }),
  filesize: t.Integer({ minimum: 0, description: "bytes" }),
  rating: nullable(t.Number({ minimum: 0, maximum: 5 })),
  tags: t.Array(Tag),
};

const detailProps = {
  torrentcount: t.Integer({ minimum: 0 }),
  parentGid: nullable(Gid),
  firstGid: nullable(Gid),
  currentGid: nullable(Gid),
  fetchedAt: IsoDateTime,
};

const strict = { additionalProperties: false } as const;

/** Policy none or warn: carries the thumbnail address. */
export const GalleryCardOpen = t.Object(
  {
    ...baseProps,
    thumb: t.String({ description: "thumbnail address" }),
    policy: t.Union([PolicyNone, PolicyWarn]),
  },
  strict,
);
/** Policy block: NO `thumb` property exists in this schema. */
export const GalleryCardBlocked = t.Object({ ...baseProps, policy: PolicyBlock }, strict);
export const GalleryCard = t.Union([GalleryCardOpen, GalleryCardBlocked]);
export type GalleryCardType = Static<typeof GalleryCard>;

export const GalleryDetailOpen = t.Object(
  {
    ...baseProps,
    ...detailProps,
    thumb: t.String(),
    policy: t.Union([PolicyNone, PolicyWarn]),
  },
  strict,
);
export const GalleryDetailBlocked = t.Object({ ...baseProps, ...detailProps, policy: PolicyBlock }, strict);
export const GalleryDetail = t.Union([GalleryDetailOpen, GalleryDetailBlocked]);
export type GalleryDetailType = Static<typeof GalleryDetail>;

/** What the policy decided for one gallery, with no gallery fields (used by internal endpoints). */
export const PolicyVerdict = t.Union([PolicyNone, PolicyWarn, PolicyBlock]);

export const TagPolicyRule = t.Object({ tag: Tag, level: AssignablePolicyLevelSchema }, strict);
